import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import Login from './components/Login';
import Main from './components/Main';
import Logout from './components/Logout';
import { AuthProvider, useAuth, hasMaintenanceExemption } from './context/Context';
import { ThemeProvider } from './context/ThemeContext';
import Maintenance from './components/Maintenance';
import SiteDataLoader from './components/SiteDataLoader';
import ChangePassword from './components/ChangePassword';
import Loader from './components/Loader';
import { isSuperAdminRole } from './config/permissions';

const ProtectedRoute = ({ children }) => {
    const { token } = useAuth();
    if (!token) {
        return <Navigate to="/login" replace />;
    }
    return children;
};

/**
 * Forced change-password gate. While must_change_password is set, every route
 * bounces here — that is what blocks the "type a URL to bypass it" case. The
 * backend enforces the same thing server-side; this just stops the UI rendering
 * a section it is about to be 403'd out of.
 */
const PasswordGate = ({ children }) => {
    const { mustChangePassword } = useAuth();
    if (mustChangePassword) {
        return <Navigate to="/change-password" replace />;
    }
    return children;
};

/**
 * Admin Panel Maintenance gate.
 *
 * Renders the maintenance screen *in place*, on whatever path the admin asked
 * for, rather than redirecting to /maintenance. Two reasons:
 *
 *   - A redirect fired from an effect runs only after the tree has committed, so
 *     the dashboard painted and fired its API calls before being replaced. As a
 *     guard, nothing behind it ever mounts.
 *   - The URL stays where the admin typed it, so a refresh returns them there
 *     once maintenance ends instead of stranding them on /maintenance.
 *
 * Ordering matters: this sits *inside* PasswordGate, so a locked-out admin is
 * still sent to change-password first — they must be able to clear that gate
 * even during maintenance, or onboarding can never complete.
 *
 * Exempt: Super Admin (needs in to switch maintenance off) and anyone who just
 * cleared a forced password change (see grantMaintenanceExemption).
 */
const MaintenanceGate = ({ children }) => {
    const { siteData, siteDataLoaded, user } = useAuth();

    // Until site settings land we can't tell "off" from "not yet known", and
    // guessing wrong is what produced the dashboard flash.
    if (!siteDataLoaded) {
        return <Loader />;
    }

    const isUnderMaintenance = parseInt(siteData?.is_admin_maintenance) === 1;
    if (isUnderMaintenance && !isSuperAdminRole(user?.role) && !hasMaintenanceExemption()) {
        return <Maintenance />;
    }
    return children;
};

function App() {
    return (
        <ThemeProvider>
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
            <Router basename="/">
                <AuthProvider>
                    <SiteDataLoader />
                    <Routes>
                        <Route exact path="/login" element={<Login />} />
                        <Route exact path="/logout" element={<Logout />} />
                        <Route path="/maintenance" element={<Maintenance />} />
                        <Route path="/change-password" element={
                            <ProtectedRoute>
                                <ChangePassword />
                            </ProtectedRoute>
                        } />
                        <Route path="*" element={
                            <ProtectedRoute>
                                <PasswordGate>
                                    <MaintenanceGate>
                                        <Main />
                                    </MaintenanceGate>
                                </PasswordGate>
                            </ProtectedRoute>
                        } />
                    </Routes>
                </AuthProvider>
            </Router>
        </ThemeProvider>
    );
}

export default App;
